/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Section, Header, FloSearchBox } from 'components';
import Content from 'containers/common/Content';
import styles from './AlertsView.module.css';
import { Col, Row } from 'react-bootstrap';
import { ToastMessage } from 'containers';
import { ActionBar } from './ActionBar';
import { FloSimpleTabs } from 'components';
import classnames from 'classnames';
import { ListView, AlertTab } from 'model/state';
import { useTranslation } from 'react-i18next';
import { AlertList } from './AlertList';
import {
  AlarmLabel, Alert, AlertActionFilter, AlertFilter, AlertTotals, Location, LocationType, Message
} from 'model';
import { useHistory } from 'react-router-dom';
import { getRoute } from './helper/router';
import { useAlertParams } from './effect/useAlertParams';
import { useNotificationService } from './effect/useNotificationService';
import InfiniteScroll from 'react-infinite-scroll-component';
import { AlertDetailModal } from 'components/AlertDetailModal';
import { useModalState, useConfirmationPopupFunction } from 'effects';
import ConfirmationDialog from 'containers/common/ConfirmationDialog';

type TabLabelProps = {
  text: string,
  count: number,
  isActive: boolean,
}
const TabLabel: React.FC<TabLabelProps> = React.memo(({text, count, isActive}) => (
  <div>
    <span className={classnames(
      styles.tabText,
      { [styles.isTabActive]: isActive }
    )}>{text}</span>
    <span className={styles.tabCount}>{`(${count})`}</span>
  </div>
));

type AlertsViewProps = {
  alertList:              ListView<Alert>,
  isAllSelected:          boolean,
  isAnySelected:          boolean,
  selectedNumber:         number,
  setSelectAll:           (selectAll: boolean) => void,
  setSelectRow:           (select: boolean, alertId: string) => void,
  updateAlertSearch:      (search: string) => void,
  search:                 string,
  fetchAlertsByUserId:    (userId: string, filter: AlertFilter) => void,
  userId:                 string | null | undefined,
  totals:                 AlertTotals,
  currentPage:            number,
  hasMoreAlerts:          boolean,
  incrementAlertPage:     () => void,
  reinitAlertPage:        () => void,
  setCurrentAlert:        (currentAlert: Alert | null) => void,
  currentAlert:           Alert | null,
  resolveAlert:           (alert: Alert) => void,
  feedbackCriticalAlert:  (alert: Alert, feedbackType: string) => void,
  isSubmittingAction:     boolean,
  hasError:               boolean,
  openConfirmationDialog: (metadata?: { [key: string]: any }) => void,
  alertsToResolveNumber:  number,
  showMessage:            (message: Message) => void,
  clearBulkAlerts:        () => void,
  isBulkResolveEnabled:   boolean,
  fetchUserLocations:     (userId: string, types?: LocationType[], page?: number, search?: string) => void,
  userLocations:          ListView<Location>,
  updateAlertFilters:     (filters: Partial<AlertActionFilter>) => void,
  filters:                AlertActionFilter;
  alarmList:              ListView<AlarmLabel>;
  fetchAlarmList:         () => void;
}

export const AlertsView: React.FC<AlertsViewProps> = React.memo(({
  alertList,
  isAllSelected,
  search,
  updateAlertSearch,
  setSelectAll,
  setSelectRow,
  fetchAlertsByUserId,
  userId,
  totals,
  currentPage,
  hasMoreAlerts,
  incrementAlertPage,
  reinitAlertPage,
  setCurrentAlert,
  currentAlert,
  resolveAlert,
  feedbackCriticalAlert,
  isSubmittingAction,
  hasError,
  isAnySelected,
  selectedNumber,
  openConfirmationDialog,
  alertsToResolveNumber,
  showMessage,
  clearBulkAlerts,
  isBulkResolveEnabled,
  fetchUserLocations,
  userLocations,
  updateAlertFilters,
  filters,
  alarmList,
  fetchAlarmList,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const params = useAlertParams();
  useNotificationService(fetchAlertsByUserId, userId, params.currentTab, currentPage, search, isSubmittingAction, hasError, filters);

  useEffect(() => {
    if (userId) {
      fetchUserLocations(userId);
    }
  }, [userId]);

  useEffect(() => {
    fetchAlarmList(); // init alarm filter dropdown

    return function cleanup() {
      reinitAlertPage();
      updateAlertSearch('');
      updateAlertFilters({locationIds: []});
    };
  }, []);

  const [showDetailModal, setShowDetailModal] = useModalState(isSubmittingAction, hasError);
  const [confirmationData, handleOpenConfirmationPopup] = useConfirmationPopupFunction(openConfirmationDialog);

  const handleAlertSelect = (alert: Alert) => {
    setCurrentAlert(alert);
    setShowDetailModal(true);
  };

  const handleUpdateSearch = (search: string) => {
    reinitAlertPage();
    updateAlertFilters({locationIds: [], alarmNames: [], activeOnly: false});
    updateAlertSearch(search);
  };

  const handleUpdateAlertFilters = (filters: Partial<AlertActionFilter>) => {
    reinitAlertPage();
    updateAlertSearch('');
    updateAlertFilters(filters);
  };

  return (
    <Section>
      { !showDetailModal && <ToastMessage /> }
      <Header className={styles.header} >
        <Col xs="12" lg="9">
          <h2>{t('alerts')}</h2>
        </Col>
        <Col xs="12" lg="3">
          <FloSearchBox
            value       = {search}
            onChange    = {handleUpdateSearch}
            placeholder = {t('Search')}
            className   = {styles.searchBox}
          />
        </Col>
      </Header>
      <Row>
        <Col xs="12" className="pl-0 pr-0">
          <FloSimpleTabs
            tabs = {[
              { id: 'all', label: isActive => <TabLabel text={t('All')} count={totals.all} isActive={isActive} /> },
              { id: 'critical', label: isActive => <TabLabel text={t('Critical')} count={totals.critical} isActive={isActive} /> },
              { id: 'warning', label: isActive => <TabLabel text={t('Warning')} count={totals.warning} isActive={isActive} /> },
              { id: 'logs', label: isActive => <TabLabel text={t('Logs')} count={totals.logs} isActive={isActive} /> }
            ]}
            selected  = {params.currentTab}
            onSelect  = {currentTab => {
              reinitAlertPage();
              history.push(getRoute({ ...params, currentTab: currentTab as AlertTab }));
              updateAlertSearch('');
            }}
          >
            <Row>
              <Col xs="12">
                <ActionBar
                  isAnySelected         = {isAnySelected}
                  selectedNumber        = {selectedNumber}
                  openConfirmationPopup = {handleOpenConfirmationPopup}
                  alertsToResolveNumber = {alertsToResolveNumber}
                  showMessage           = {showMessage}
                  clearBulkAlerts       = {clearBulkAlerts}
                  isBulkResolveEnabled  = {isBulkResolveEnabled}
                  userLocations         = {userLocations}
                  fetchUserLocations    = {fetchUserLocations}
                  userId                = {userId}
                  currentTab            = {params.currentTab}
                  search                = {search}
                  updateAlertFilters    = {handleUpdateAlertFilters}
                  filters               = {filters}
                  alarmList             = {alarmList}
                />
              </Col>
            </Row>
            <Content>
              <Col xs="12" className={styles.contentWrapper}>
                <InfiniteScroll
                  className       = {styles.infiniteScroll}
                  dataLength      = {alertList.rows?.length || 0}
                  next            = {incrementAlertPage}
                  hasMore         = {hasMoreAlerts}
                  loader          = {<div className={styles.loader}><span className="spinner-border" role="status" aria-hidden="true"></span></div>}
                  endMessage      = {
                    <p style={{textAlign: 'center', marginTop: 30}}>
                      <b>{t('infinite-scroll-end-message')}</b>
                    </p>
                  }
                >
                  <AlertList
                    alertList     = {alertList}
                    isSelectedAll = {isAllSelected}
                    setSelectAll  = {setSelectAll}
                    setSelectRow  = {setSelectRow}
                    onSelect      = {handleAlertSelect}
                  />
                </InfiniteScroll>
              </Col>
            </Content>
          </FloSimpleTabs>
          <AlertDetailModal
            show          = {showDetailModal}
            onHide        = {() => { setShowDetailModal(false) }}
            alert         = {currentAlert}
            onResolve     = {resolveAlert}
            onFeedbackCriticalAlert = {feedbackCriticalAlert}
            isSubmitting  = {isSubmittingAction}
          />
          <ConfirmationDialog
            title       = {confirmationData.title}
            description = {confirmationData.description}
            actionText  = {confirmationData.actionText}
            onAccept    = {confirmationData.onAccept}
          />
        </Col>
      </Row>
    </Section>
  )
})
